import React, { useState } from 'react';
import { Heart, ChevronDown, Lock } from 'lucide-react';
import CatalogProductImage from './CatalogProductImage';

/**
 * ProductChatCard — in-chat product analysis, redesigned from the menu view.
 *
 * `access` controls the free / register / paid split:
 *   - 'full'  → everything (what a registered/paid user sees)
 *   - 'guest' → FREE: header + match score + key ingredients.
 *               REGISTER (blurred): "How we scored it" + "What this means for you".
 *               PAID (blurred): "Understanding the formulation".
 */

function band(score) {
  if (score >= 90) return { t: 'Excellent', c: 'bg-[#F0F7F0] text-[#3B7A3B]' };
  if (score >= 75) return { t: 'Good', c: 'bg-[#F0F7F0] text-[#3B7A3B]' };
  if (score >= 55) return { t: 'Fair', c: 'bg-[#FFF4E5] text-[#B26B00]' };
  return { t: 'Poor', c: 'bg-[#FEECEC] text-[#C0392B]' };
}
function scoreLabel(score) {
  if (score >= 85) return 'Excellent compatibility with your skin profile.';
  if (score >= 70) return 'Good compatibility with your skin profile.';
  if (score >= 50) return 'Moderate compatibility with your skin profile.';
  return 'Limited compatibility with your skin profile.';
}

function Section({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#2A324B]/8">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between py-3.5">
        <h4 className="text-[15px] font-semibold text-[#2A324B]">{title}</h4>
        <ChevronDown className={`w-[18px] h-[18px] text-[#2A324B] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pb-3.5">{children}</div>}
    </div>
  );
}

// Blurred, locked section with an unlock CTA overlaid.
function Locked({ tier = 'register', title, cta, children }) {
  return (
    <div className="relative my-3.5 rounded-2xl overflow-hidden border border-[#2A324B]/10">
      <div className="pointer-events-none select-none px-3 py-3" style={{ filter: 'blur(5px)', opacity: 0.5 }} aria-hidden="true">
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-2 px-6"
           style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.45), rgba(255,255,255,0.85))' }}>
        <div className="w-9 h-9 rounded-full bg-[#2A324B] flex items-center justify-center">
          <Lock className="w-[15px] h-[15px] text-[#F3D5B5]" />
        </div>
        {tier === 'paid' && <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#C8956A]">Premium</span>}
        <p className="text-[13.5px] font-semibold text-[#2A324B] leading-snug max-w-[250px]">{title}</p>
        <button className="mt-0.5 bg-[#2A324B] text-[#F3D5B5] rounded-full px-4 py-2 text-[12.5px] font-semibold">{cta}</button>
      </div>
    </div>
  );
}

export default function ProductChatCard({ product = {}, access = 'full' }) {
  const {
    name = 'Unknown Product', brand = '', category = '', image, score = 0,
    scoring = [], keyIngredients = [], keyIngredientsMore = 0, formulation = [], whatItMeans = [],
  } = product;
  const guest = access === 'guest';

  const Header = (
    <div className="flex items-start gap-3 mb-3.5">
      <div className="w-[60px] h-[60px] rounded-xl flex-none overflow-hidden ring-1 ring-black/5 bg-[#E8DCCB]">
        <CatalogProductImage src={image} alt={name} brand={brand} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[16.5px] font-semibold text-[#2A324B] leading-tight">{name}</div>
        {brand && <div className="text-[13.5px] text-[#2A324B]/60 mt-0.5">{brand}</div>}
        {category && <span className="inline-block text-xs text-[#2A324B] border border-[#2A324B]/12 rounded-full px-2.5 py-[3px] mt-2">{category}</span>}
      </div>
      <button className="w-9 h-9 rounded-full bg-[#F3EFEA] flex items-center justify-center flex-none text-[#2A324B]" aria-label="Save">
        <Heart className="w-[18px] h-[18px]" />
      </button>
    </div>
  );

  const Score = (
    <div className="flex items-center gap-4 bg-[#2A324B] rounded-2xl p-[17px] mb-1.5">
      <div className="relative w-[78px] h-[78px] flex-none">
        <svg width="78" height="78" viewBox="0 0 78 78" className="-rotate-90">
          <circle cx="39" cy="39" r="32" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="6" />
          <circle cx="39" cy="39" r="32" fill="none" stroke="#F3D5B5" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 32} strokeDashoffset={(2 * Math.PI * 32) * (1 - score / 100)} />
        </svg>
        <span className="absolute inset-0 flex items-baseline justify-center text-white font-bold">
          <span className="text-[22px]">{score}</span><span className="text-xs">%</span>
        </span>
      </div>
      <div>
        <div className="text-white text-base font-semibold leading-tight">{scoreLabel(score)}</div>
        <div className="text-[#F3D5B5] text-[12.5px] font-semibold mt-1.5">Powered by Son:mi</div>
      </div>
    </div>
  );

  const KeyIngredients = keyIngredients.length > 0 && (
    <div className="mt-4">
      <p className="text-xs font-bold tracking-[0.14em] uppercase text-[#2A324B]/40 mb-2.5">Key Ingredients</p>
      <div className="flex flex-wrap gap-[7px]">
        {keyIngredients.map((k, i) => <span key={i} className="text-[13px] text-[#2A324B] border border-[#2A324B]/12 rounded-full px-3 py-[5px]">{k}</span>)}
        {keyIngredientsMore > 0 && <span className="text-[13px] text-[#2A324B]/55 bg-[#EFEAE4] rounded-full px-3 py-[5px]">+{keyIngredientsMore} more</span>}
      </div>
    </div>
  );

  const scoringRows = (
    <>
      <p className="text-[13px] text-[#2A324B]/50 mb-3">Your match score is the weighted average of these.</p>
      {scoring.map((d, i) => {
        const b = band(d.score);
        return (
          <div key={i} className={`py-2.5 ${i > 0 ? 'border-t border-[#2A324B]/8' : ''}`}>
            <div className="flex items-center justify-between gap-2.5">
              <span className="text-[15px] text-[#2A324B]">{d.label}</span>
              <span className={`text-[12.5px] font-semibold rounded-full px-2.5 py-1 flex-none ${b.c}`}>{b.t} · {d.score}</span>
            </div>
            {d.reason && <p className="text-[13px] text-[#2A324B]/50 mt-1 leading-snug">{d.reason}</p>}
          </div>
        );
      })}
    </>
  );

  const formulationList = (
    <ul className="m-0 p-0">
      {formulation.map((f, i) => (
        <li key={i} className="flex gap-2.5 py-1.5 text-[13.5px] text-[#2A324B]/60 leading-relaxed">
          <span className="w-[5px] h-[5px] rounded-full bg-[#C8956A] flex-none mt-[9px]" />
          <span><span className="font-semibold text-[#2A324B]">{f.lead}</span> {f.rest}</span>
        </li>
      ))}
    </ul>
  );

  const whatMeansRows = whatItMeans.map((m, i) => (
    <div key={i} className="flex gap-2.5 py-1.5 text-sm text-[#2A324B]/80 leading-snug">
      <span className={`w-[18px] h-[18px] rounded-full flex-none mt-0.5 flex items-center justify-center border-2 ${m.tone === 'caution' ? 'border-[#f5b53f]' : 'border-[#4ade80]'}`}>
        <span className={`w-[7px] h-[7px] rounded-full ${m.tone === 'caution' ? 'bg-[#f5b53f]' : 'bg-[#4ade80]'}`} />
      </span>
      <span>{m.text}</span>
    </div>
  ));

  return (
    <div className="bg-white rounded-2xl border border-[#2A324B]/12 shadow-sm overflow-hidden">
      <div className="p-[15px]">
        {Header}
        {Score}
        {KeyIngredients}

        {guest ? (
          <>
            <Locked tier="register" title="See how it scored &amp; what it means for your skin" cta="Create a free account">
              <h4 className="text-[15px] font-semibold text-[#2A324B] mb-1">How we scored it</h4>
              {scoringRows}
              <h4 className="text-[15px] font-semibold text-[#2A324B] mt-3 mb-1">What this means for you</h4>
              {whatMeansRows}
            </Locked>
            <Locked tier="paid" title="The full ingredient-by-ingredient breakdown" cta="Unlock formulation">
              <h4 className="text-[15px] font-semibold text-[#2A324B] mb-1">Understanding the formulation</h4>
              {formulationList}
            </Locked>
          </>
        ) : (
          <>
            <div className="mt-2"><Section title="How we scored it" defaultOpen>{scoringRows}</Section></div>
            <div className="mt-3.5"><Section title="Understanding the formulation">{formulationList}</Section></div>
            <div className="pt-3.5">
              <h4 className="text-[15px] font-semibold text-[#2A324B] mb-3">What this means for you</h4>
              {whatMeansRows}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
